import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { workerAPI, labAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const WorkerDetail = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [worker, setWorker] = useState(null);
  const [activeTasks, setActiveTasks] = useState([]);
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    pendingTasks: 0,
  });

  useEffect(() => {
    loadWorkerDetails();
  }, [id]);

  const loadWorkerDetails = async () => {
    try {
      setLoading(true);
      const [workerResponse, tasksResponse] = await Promise.all([
        workerAPI.getOne(id),
        workerAPI.getActiveTasks(id),
      ]);

      const workerData = workerResponse.data.data;
      const tasksData = tasksResponse.data.data;

      setWorker(workerData);
      setActiveTasks(tasksData);

      // Calculate stats
      const totalTasks = tasksData.length;
      const completedTasks = tasksData.filter(t => t.status === 'Completed').length;
      const inProgressTasks = tasksData.filter(t => t.status === 'In Progress').length;
      const pendingTasks = tasksData.filter(t => t.status === 'Pending' || t.status === 'Assigned').length;

      setStats({
        totalTasks,
        completedTasks,
        inProgressTasks,
        pendingTasks,
      });
    } catch (error) {
      showError(t('messages.error'));
      navigate('/workers');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-gray-100 text-gray-800',
      'Assigned': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'On Hold': 'bg-orange-100 text-orange-800',
      'Cancelled': 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="text-center">
        <p className="text-gray-500">{t('workers.notFound')}</p>
        <button onClick={() => navigate('/workers')} className="mt-4 text-blue-600 hover:text-blue-900">
          {t('workers.backToWorkers')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{worker.full_name}</h2>
          <p className="text-gray-600 mt-1">{t('workers.profileSubtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/workers')}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
        >
          {t('workers.backToList')}
        </button>
      </div>

      {/* Worker Info Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workers.contactInformation')}</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600">{t('workers.email')}</label>
              <p className="font-medium text-gray-900">{worker.email}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">{t('workers.phone')}</label>
              <p className="font-medium text-gray-900">{worker.phone}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">{t('workers.hourlyRate')}</label>
              <p className="font-medium text-gray-900">{t('common.currency')} {parseFloat(worker.hourly_rate).toFixed(2)}/{t('common.hour')}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">{t('common.status')}</label>
              <p className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${
                worker.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {worker.is_active ? t('workers.status.active') : t('workers.status.inactive')}
              </p>
            </div>
          </div>
        </div>

        {/* Skills Card */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workers.skills')}</h3>
          <div className="flex flex-wrap gap-2">
            {worker.skills && worker.skills.split(',').map((skill, idx) => (
              <span
                key={idx}
                className="px-3 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
              >
                {skill.trim()}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Workload Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow p-6 border-l-4 border-blue-600">
          <div className="text-sm text-blue-600 font-semibold uppercase tracking-wide">{t('lab.totalTasks')}</div>
          <div className="text-3xl font-bold text-blue-900 mt-2">{stats.totalTasks}</div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl shadow p-6 border-l-4 border-yellow-600">
          <div className="text-sm text-yellow-600 font-semibold uppercase tracking-wide">{t('lab.tasksInProgressLabel')}</div>
          <div className="text-3xl font-bold text-yellow-900 mt-2">{stats.inProgressTasks}</div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow p-6 border-l-4 border-green-600">
          <div className="text-sm text-green-600 font-semibold uppercase tracking-wide">{t('lab.tasksCompletedLabel')}</div>
          <div className="text-3xl font-bold text-green-900 mt-2">{stats.completedTasks}</div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow p-6 border-l-4 border-purple-600">
          <div className="text-sm text-purple-600 font-semibold uppercase tracking-wide">{t('lab.tasksPendingLabel')}</div>
          <div className="text-3xl font-bold text-purple-900 mt-2">{stats.pendingTasks}</div>
        </div>
      </div>

      {/* Active Tasks */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workers.assignedTasks')}</h3>
        
        {activeTasks.length === 0 ? (
          <p className="text-gray-500 text-center py-8">{t('workers.noAssignedTasks')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('lab.labNumber')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('lab.salesOrder')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('lab.item')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('lab.started')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {activeTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {task.lab_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      SO-{task.sales_order_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {task.sales_order_item?.product?.name || t('measurements.na')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(task.status)}`}>
                        {task.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {task.started_at ? new Date(task.started_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => navigate(`/lab/${task.id}`)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        {t('common.viewDetails')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerDetail;
